import { IColumnTypes, IResultFieldValidation } from 'components/spreadsheet/types'
import { AllowedDateFormats, AllowedDatetimeFormats, ICellValue, ITableViewColumn } from 'types'
import { COLUMN_TYPE_NAMES, NUMBER_COLUMN_TYPES, SELECT_COLUMN_TYPES, TEXT_COLUMN_TYPES } from '../constants/const'
import { isSameValueStringValue, validateField } from './functions'
import { toUTCString } from './format'
import { validateJsonObject } from './jsonCell'

export const reformattedPasteData = (text: string) => {
  const trimmedText = text.replace(/\n+$/, '')

  const rows: string[] = []
  let currentRow = ''
  let inQuotes = false

  for (let i = 0; i < trimmedText.length; i++) {
    const char = trimmedText[i]

    if (char === '"') {
      if (inQuotes) {
        if (i + 1 < trimmedText.length && trimmedText[i + 1] === '"') {
          // Escaped quote ("") inside a quoted field
          currentRow += '""'
          i++
        } else {
          // Closing quote
          inQuotes = false
          currentRow += char
        }
      } else {
        // Only treat " as opening a quoted field when at a field boundary
        const prevChar = i > 0 ? trimmedText[i - 1] : null
        if (prevChar === null || prevChar === '\t' || prevChar === '\n') {
          inQuotes = true
        }
        currentRow += char
      }
    } else if (char === '\n' && !inQuotes) {
      rows.push(currentRow)
      currentRow = ''
    } else {
      currentRow += char
    }
  }

  if (currentRow) {
    rows.push(currentRow)
  }

  return rows
}

function splitByDelimiters(value: string, delimiters: string[] = [',', ';', '|']): string[] {
  for (const delimiter of delimiters) {
    if (value.includes(delimiter)) {
      return value.split(delimiter).map((v) => v.trim())
    }
  }
  // If no delimiter matched, return single-item array
  return [value.trim()]
}

type TemporalKind = 'date' | 'datetime'

type Key = 'year' | 'month' | 'monthName' | 'day' | 'hour' | 'minute' | 'second' | 'ampm' | null

type TokenDef = { token: string; re: string; key?: Key }

// Shared token set (date + time)
const TOKENS: TokenDef[] = [
  // Years
  { token: 'YYYY', re: '(\\d{4})', key: 'year' },
  { token: 'YYY', re: '(\\d{3,4})', key: 'year' },
  { token: 'YY', re: '(\\d{2})', key: 'year' },

  // Months (names)
  { token: 'MMMM', re: '([A-Za-z]{3,9})', key: 'monthName' },
  { token: 'MMM', re: '([A-Za-z]{3})', key: 'monthName' },

  // Months (numbers)
  { token: 'MM', re: '(\\d{2})', key: 'month' },
  { token: 'M', re: '(\\d{1,2})', key: 'month' },

  // Days
  { token: 'DD', re: '(\\d{2})', key: 'day' },
  { token: 'D', re: '(\\d{1,2})', key: 'day' },

  // Time
  { token: 'HH', re: '(\\d{2})', key: 'hour' },
  { token: 'H', re: '(\\d{1,2})', key: 'hour' },
  { token: 'mm', re: '(\\d{2})', key: 'minute' },
  { token: 'm', re: '(\\d{1,2})', key: 'minute' },
  { token: 'ss', re: '(\\d{2})', key: 'second' },
  { token: 's', re: '(\\d{1,2})', key: 'second' },

  // AM/PM
  { token: 'A', re: '(AM|PM)', key: 'ampm' },
  { token: 'a', re: '(am|pm)', key: 'ampm' },

  // Weekdays (ignored)
  { token: 'DDDD', re: '(?:[A-Za-z]{3,9})', key: null },
  { token: 'DDD', re: '(?:[A-Za-z]{3})', key: null },

  // Flexible delimiter token (ignored)
  { token: '[-/.]', re: '(?:[-\\/\\.])', key: null }
]

const TOKENS_LONGEST_FIRST = [...TOKENS].sort((a, b) => b.token.length - a.token.length)

// Month name normalization
const MONTHS: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11
}

function escapeLiteral(ch: string) {
  return ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Shared cache for compiled formats
const formatCache = new Map<string, { regex: RegExp; groupKeys: Array<Exclude<Key, null>> }>()

function compileFormat(format: string) {
  const cached = formatCache.get(format)
  if (cached) return cached

  let i = 0
  let regexStr = '^'
  const groupKeys: Array<Exclude<Key, null>> = []

  while (i < format.length) {
    const tokenMatch = TOKENS_LONGEST_FIRST.find((t) => format.startsWith(t.token, i))

    if (tokenMatch) {
      regexStr += tokenMatch.re
      if (tokenMatch.key) groupKeys.push(tokenMatch.key as Exclude<Key, null>)
      i += tokenMatch.token.length
      continue
    }

    const ch = format[i]

    // whitespace in format = flexible whitespace
    if (/\s/.test(ch)) {
      while (i < format.length && /\s/.test(format[i])) i++
      regexStr += '\\s*'
      continue
    }

    regexStr += escapeLiteral(ch)
    i++
  }

  regexStr += '$'
  const compiled = { regex: new RegExp(regexStr, 'i'), groupKeys }
  formatCache.set(format, compiled)
  return compiled
}

// --------- ONE unified parser ----------
export function parseTemporalByFormat(value: string, format: string, opts: { kind: TemporalKind }): Date | null {
  let trimmed = value.trim()
  if (!trimmed) return null

  // Strip fractional seconds (e.g. ".787") — no format includes sub-second precision
  if (opts.kind === 'datetime') {
    trimmed = trimmed.replace(/(\d{2}:\d{2}:\d{2})\.\d+/, '$1')
  }

  const { regex, groupKeys } = compileFormat(format)

  const m = trimmed.match(regex)
  if (!m) return null

  const captures = m.slice(1)

  let year: number | undefined
  let month: number | undefined // 0-based
  let day: number | undefined
  let hour: number | undefined
  let minute: number | undefined
  let second: number = 0
  let ampm: string | undefined

  for (let idx = 0; idx < captures.length; idx++) {
    const key = groupKeys[idx]
    const raw = captures[idx].trim()

    switch (key) {
      case 'year':
        year = parseInt(raw, 10)
        // 2-digit year pivot: 00-49 → 2000-2049, 50-99 → 1950-1999
        if (raw.length === 2) year += year < 50 ? 2000 : 1900
        break
      case 'month':
        month = parseInt(raw, 10) - 1
        break
      case 'monthName': {
        const mm = MONTHS[raw.toLowerCase()]
        if (mm === undefined) return null
        month = mm
        break
      }
      case 'day':
        day = parseInt(raw, 10)
        break
      case 'hour':
        hour = parseInt(raw, 10)
        break
      case 'minute':
        minute = parseInt(raw, 10)
        break
      case 'second':
        second = parseInt(raw, 10)
        break
      case 'ampm':
        ampm = raw.toLowerCase()
        break
    }
  }

  // Required fields by kind
  if (year == null || month == null || day == null) return null
  if (opts.kind === 'datetime' && (hour == null || minute == null)) return null

  // Interpret AM/PM if present
  if (ampm && hour != null) {
    if (hour < 1 || hour > 12) return null
    if (ampm === 'pm' && hour !== 12) hour += 12
    if (ampm === 'am' && hour === 12) hour = 0
  }

  // Range checks (date)
  if (month < 0 || month > 11) return null
  if (day < 1 || day > 31) return null

  // Range checks (time if datetime)
  if (opts.kind === 'datetime') {
    if (hour! < 0 || hour! > 23) return null
    if (minute! < 0 || minute! > 59) return null
    if (second < 0 || second > 59) return null
  }

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null

  const date =
    opts.kind === 'datetime'
      ? new Date(year, month, day, hour!, minute!, second) // local time
      : new Date(Date.UTC(year, month, day)) // floating date

  // Rollover checks
  if (opts.kind === 'datetime') {
    // validate using LOCAL getters
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month ||
      date.getDate() !== day ||
      date.getHours() !== hour ||
      date.getMinutes() !== minute ||
      date.getSeconds() !== second
    )
      return null
  } else {
    // validate using UTC getters
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month || date.getUTCDate() !== day) return null
  }

  return date
}

// Paste fallbacks — generated from primitive parts rather than enumerated by
// hand so that all permutations of ordering, year width, and separator are
// covered without judgement calls.
//
// We deliberately omit purely-numeric DD/MM vs MM/DD permutations: adding
// those would silently flip ambiguous values like "01/02/2025". The only
// numeric fallback is ISO (year-first), where the ordering is fixed by
// convention. Everything else requires a month NAME (MMMM, which matches
// 3-9 letters and therefore handles both "May" and "January").
const FALLBACK_YEAR_FORMS = ['YYYY', 'YY']
const FALLBACK_FLEX_SEP = '[-/.]' // registered TOKENS entry — matches '-', '/' or '.'
const FALLBACK_TIME_PARTS = ['HH:mm:ss', 'HH:mm']

function buildPasteDateFormats(): string[] {
  const formats: string[] = []

  // ISO numeric (year-first is unambiguous by convention)
  formats.push(`YYYY${FALLBACK_FLEX_SEP}M${FALLBACK_FLEX_SEP}D`)
  formats.push('YYYY M D')

  // Y - month name - D — only 4-digit year. "YY MMMM D" would be ambiguous
  // with "D MMMM YY" (e.g. "25-May-10" — is 25 the year or the day?).
  formats.push(`YYYY${FALLBACK_FLEX_SEP}MMMM${FALLBACK_FLEX_SEP}D`)
  formats.push('YYYY MMMM D')

  // D-M-Y and M-D-Y with month name — both year widths.
  for (const y of FALLBACK_YEAR_FORMS) {
    formats.push(`D${FALLBACK_FLEX_SEP}MMMM${FALLBACK_FLEX_SEP}${y}`) // 10-May-2025
    formats.push(`D MMMM ${y}`) // 10 May 2025
    formats.push(`MMMM${FALLBACK_FLEX_SEP}D${FALLBACK_FLEX_SEP}${y}`) // May-10-2025
    formats.push(`MMMM D ${y}`) // May 10 2025
    formats.push(`MMMM D, ${y}`) // May 10, 2025
  }

  return formats
}

function buildPasteDatetimeFormats(dateFormats: string[]): string[] {
  const formats: string[] = []

  // ISO with literal 'T' separator (only sensible after ISO date).
  for (const t of FALLBACK_TIME_PARTS) {
    formats.push(`YYYY${FALLBACK_FLEX_SEP}M${FALLBACK_FLEX_SEP}DT${t}`)
  }

  // Space-separated date + time for every supported date format.
  for (const d of dateFormats) {
    for (const t of FALLBACK_TIME_PARTS) {
      formats.push(`${d} ${t}`)
    }
  }

  return formats
}

export const PASTE_DATE_FALLBACK_FORMATS: string[] = buildPasteDateFormats()
export const PASTE_DATETIME_FALLBACK_FORMATS: string[] = buildPasteDatetimeFormats(PASTE_DATE_FALLBACK_FORMATS)

function parseDateFlexible(value: string, primaryFormat?: AllowedDateFormats): Date | null {
  if (primaryFormat) {
    const direct = parseTemporalByFormat(value, primaryFormat, { kind: 'date' })
    if (direct) return direct
  }
  for (const fmt of PASTE_DATE_FALLBACK_FORMATS) {
    if (fmt === primaryFormat) continue
    const parsed = parseTemporalByFormat(value, fmt, { kind: 'date' })
    if (parsed) return parsed
  }
  return null
}

function parseDatetimeFlexible(value: string, primaryFormat?: AllowedDatetimeFormats): Date | null {
  if (primaryFormat) {
    const direct = parseTemporalByFormat(value, primaryFormat, { kind: 'datetime' })
    if (direct) return direct
  }
  for (const fmt of PASTE_DATETIME_FALLBACK_FORMATS) {
    if (fmt === primaryFormat) continue
    const parsed = parseTemporalByFormat(value, fmt, { kind: 'datetime' })
    if (parsed) return parsed
  }
  return null
}

// Messages for pasted values that can't be converted to the column's type. The paste handler shows
// the thrown string to the user as-is, so these name the column type the way the app does.
const cannotPasteMessage = (value: string, kind: IColumnTypes) =>
  `'${value}' can't be pasted into this ${COLUMN_TYPE_NAMES[kind]} column.`

const notANumberMessage = (value: string, kind: IColumnTypes) =>
  `'${value}' isn't a number, so it can't be pasted into this ${COLUMN_TYPE_NAMES[kind]} column.`

const wrongDateFormatMessage = (value: string, format: string) =>
  `'${value}' doesn't match this column's date format (${format}). Paste dates in that format.`

const notADateMessage = (value: string, kind: string) =>
  `'${value}' isn't a date Morta recognises. Paste ${kind === 'datetime' ? 'dates and times' : 'dates'} like 2026-12-31.`

export const transformStringToCorrectFormat = (
  value: string | null,
  kind: IColumnTypes,
  dateFormat?: AllowedDateFormats,
  datetimeFormat?: AllowedDatetimeFormats
) => {
  if (value === null || value === '') return null
  else {
    if (kind === 'multilink' || kind === 'multiselect') {
      let array: any[]
      try {
        array = JSON.parse(value)
      } catch {
        array = splitByDelimiters(value)
      }

      if (!Array.isArray(array)) {
        array = [value.toString().trim()]
      }

      // Flatten one level of nesting — joined multiselect columns can produce [["Yes"]]
      // instead of ["Yes"] when aggregated via jsonb_agg over an already-array source column.
      array = array.flat()

      array.forEach((item) => {
        if (typeof item !== 'string') {
          throw `'${value}' can't be pasted into this ${COLUMN_TYPE_NAMES[kind]} column. Paste plain text, with each item separated by a comma.`
        }
        return item.trim()
      })
      return array
    } else if (kind === 'attachment' || kind === 'vote') {
      let array
      try {
        array = JSON.parse(value)
      } catch {
        throw cannotPasteMessage(value, kind)
      }
      if (!Array.isArray(array)) throw cannotPasteMessage(value, kind)
      return array
    } else if (kind === 'json') {
      // Parse pasted text into a top-level JSON object so the cell holds an object
      // (not a raw string) — updateTableViewCellsAction does not re-hydrate it from
      // the API response, so the optimistic value must already be the parsed object.
      const result = validateJsonObject(value)
      if (!result.ok) throw result.error
      // json objects are narrowed at the cell boundary; IJsonCellValue is intentionally
      // not part of the ICellValue union (see types.ts), so cast here.
      return result.value as ICellValue
    } else if (kind === 'integer' || kind === 'duration') {
      const number = Math.round(Number(value.replace(/,/g, '')))
      if (isNaN(number)) throw notANumberMessage(value, kind)
      return number
    } else if (kind === 'float' || kind === 'percentage') {
      const number = Number.parseFloat(value.replace(/,/g, ''))
      if (isNaN(number)) throw notANumberMessage(value, kind)
      return number
    } else if (kind === 'checkbox') {
      if (value && (value.toLocaleLowerCase() === 'true' || value === '1')) return 'true'
      else return 'false'
    } else if (kind === 'date') {
      const date = parseDateFlexible(value, dateFormat)
      if (date) return date.toISOString().split('T')[0]
      if (!dateFormat) {
        checkStringIsValidDatetime(value, kind)
        return value
      }
      throw wrongDateFormatMessage(value, dateFormat)
    } else if (kind === 'datetime') {
      const date = parseDatetimeFlexible(value, datetimeFormat)
      if (date) return toUTCString(date.toISOString(), kind)
      if (!datetimeFormat) {
        checkStringIsValidDatetime(value, kind)
        return value
      }
      throw wrongDateFormatMessage(value, datetimeFormat)
    } else {
      return value
    }
  }
}

export const validatePasteField = (value: ICellValue, column: ITableViewColumn) => {
  const kind = column.kind
  // `validateField` has no 'json' branch (it returns { pass: false }); json paste
  // values are validated/coerced by the json transform path, so skip it here too —
  // matching the same exclusion in Cell.setCurrentValue.
  if (
    kind !== 'multilink' &&
    kind !== 'attachment' &&
    kind !== 'select' &&
    kind !== 'multiselect' &&
    kind !== 'vote' &&
    kind !== 'json'
  ) {
    const result = validateField(kind, value, column.name)
    if (!(result as IResultFieldValidation).pass) {
      throw (result as IResultFieldValidation).error
    }
  }

  return true
}

// The first field of one TSV row, unquoted. A quoted field runs to its closing quote, so tabs and
// newlines inside it stay part of the value; one that isn't closed, or has text after its closing
// quote, is taken literally up to the first tab.
const firstTsvField = (row: string): string => {
  const quoted = /^"((?:[^"]|"")*)"(?:\t|$)/.exec(row)
  return quoted ? quoted[1].replace(/""/g, '"') : row.split('\t')[0]
}

// Clipboard text pasted into a single cell, such as a form field: the first field of whatever was
// copied, unquoted, then converted to the column's kind with the grid paste's own conversion.
// Throws when the text isn't a valid value for the column.
export const parseSingleCellPaste = (text: string, column: ITableViewColumn): ICellValue => {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const field = firstTsvField(reformattedPasteData(normalized)[0] ?? '')

  const format = column.dateFormat ?? undefined
  const dateFormat = column.kind === 'date' ? (format as AllowedDateFormats | undefined) : undefined
  const datetimeFormat = column.kind === 'datetime' ? (format as AllowedDatetimeFormats | undefined) : undefined

  const value = transformStringToCorrectFormat(field, column.kind, dateFormat, datetimeFormat)
  validatePasteField(value, column)
  return value
}

export const checkValuesSame = (oldValue: ICellValue, newValue: ICellValue, kind: IColumnTypes) => {
  // Fast path: identical primitives or same reference — covers date, datetime, checkbox,
  // and any other types not explicitly handled below
  if (oldValue === newValue) return true

  if ([...SELECT_COLUMN_TYPES, 'multilink', 'attachment', 'vote'].includes(kind)) {
    // if both are arrays, we need to check if the values are the same
    if (Array.isArray(newValue) && Array.isArray(oldValue)) {
      if ((newValue as string[]).length === (oldValue as string[]).length) {
        const isNewValue = (newValue as string[])
          .map((item) => Array.isArray(oldValue) && (oldValue as string[]).includes(item))
          .some((e) => e === false)
        if (!isNewValue) {
          return true
        }
      }
    }
  } else if (
    [...TEXT_COLUMN_TYPES, ...NUMBER_COLUMN_TYPES, 'link'].includes(kind) &&
    isSameValueStringValue(newValue as string, oldValue as string)
  ) {
    return true
  } else if (kind === 'json') {
    // JSON cell values are objects, so the reference check above never matches even
    // when the content is unchanged. Compare by serialised form so re-committing an
    // identical payload doesn't trigger a needless save/audit update.
    try {
      return JSON.stringify(oldValue) === JSON.stringify(newValue)
    } catch {
      return false
    }
  }
  return false
}

export const checkStringIsValidDatetime = (value: string, kind: string) => {
  const parsedDate = Date.parse(value)
  if (isNaN(parsedDate)) throw notADateMessage(value, kind)
  const toIsoString = new Date(parsedDate).toISOString().split('T')[0]
  if (!value.startsWith(toIsoString)) throw notADateMessage(value, kind)
  return value
}
